import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { objectsApi, type AnomalySizePoint } from '../api'
import { appLocale } from '@/lib/dateUtils'
import { escapeHtml } from '@/lib/escape-html'

interface AnomalySizeChartDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function AnomalySizeChartDialog({ open, onOpenChange, objectID }: AnomalySizeChartDialogProps) {
    const { t } = useTranslation()
    const [data, setData] = useState<AnomalySizePoint[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open || !objectID) return
        setLoading(true)
        objectsApi.getAnomalySizeChart(objectID)
            .then((res) => {
                const bd = res.data as any
                setData(bd?.data || bd || [])
            })
            .catch(() => setData([]))
            .finally(() => setLoading(false))
    }, [open, objectID])

    const chartOption = useMemo((): EChartsOption => {
        if (data.length === 0) return {}

        const dates = data.map((d) => {
            const dt = new Date(d.datetime)
            return dt.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric', year: '2-digit' })
        })
        const sizes = data.map((d) => d.size || 0)
        const upperBounds = data.map((d) => d.upperBound || 0)
        const lowerBounds = data.map((d) => d.lowerBound || 0)
        const meanSizes = data.map((d) => d.meanSize || 0)

        // Mark anomaly points
        const anomalyIndices = data
            .map((d, i) => (d.anomaly && d.anomaly.toLowerCase() === 'yes' ? i : -1))
            .filter((i) => i >= 0)

        return {
            tooltip: {
                trigger: 'axis',
                formatter: (params: any) => {
                    if (!Array.isArray(params) || params.length === 0) return ''
                    const idx = params[0].dataIndex
                    const point = data[idx]
                    const isAnomaly = point?.anomaly?.toLowerCase() === 'yes'
                    let html = `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(dates[idx])}</div>`
                    html += `<div>Size: <b>${formatBytes(sizes[idx])}</b></div>`
                    html += `<div style="color:#888">Mean: ${formatBytes(meanSizes[idx])}</div>`
                    html += `<div style="color:#888">Upper: ${formatBytes(upperBounds[idx])}</div>`
                    html += `<div style="color:#888">Lower: ${formatBytes(lowerBounds[idx])}</div>`
                    if (point?.backupType) html += `<div style="color:#888">Type: ${escapeHtml(point.backupType)}</div>`
                    if (isAnomaly) html += `<div style="color:#f97316;font-weight:600;margin-top:4px">Anomaly detected</div>`
                    return html
                },
            },
            legend: {
                bottom: 0,
                data: ['Size', 'Mean', 'Upper Bound', 'Lower Bound'],
                textStyle: { fontSize: 11 },
                itemWidth: 14,
                itemHeight: 8,
            },
            grid: { left: 15, right: 15, top: 15, bottom: 40, containLabel: true },
            xAxis: {
                type: 'category',
                data: dates,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { fontSize: 10, color: '#888', rotate: 30 },
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
                axisLabel: {
                    fontSize: 10,
                    color: '#888',
                    formatter: (v: number) => formatBytes(v),
                },
            },
            series: [
                // Confidence band (upper - lower filled area)
                {
                    name: 'Lower Bound',
                    type: 'line',
                    data: lowerBounds,
                    lineStyle: { opacity: 0 },
                    symbol: 'none',
                    stack: 'confidence',
                    areaStyle: { opacity: 0 },
                },
                {
                    name: 'Upper Bound',
                    type: 'line',
                    data: upperBounds.map((u, i) => u - lowerBounds[i]),
                    lineStyle: { width: 1, type: 'dashed', color: '#d1d5db' },
                    symbol: 'none',
                    stack: 'confidence',
                    areaStyle: { color: 'rgba(147,197,253,0.15)' },
                },
                // Mean line
                {
                    name: 'Mean',
                    type: 'line',
                    data: meanSizes,
                    lineStyle: { width: 1.5, type: 'dashed', color: '#94a3b8' },
                    symbol: 'none',
                    z: 2,
                },
                // Actual size line
                {
                    name: 'Size',
                    type: 'line',
                    data: sizes,
                    lineStyle: { width: 2.5, color: '#3b82f6' },
                    symbol: 'circle',
                    symbolSize: (value: number, params: any) => {
                        return anomalyIndices.includes(params.dataIndex) ? 10 : 4
                    },
                    itemStyle: {
                        color: (params: any) => {
                            return anomalyIndices.includes(params.dataIndex) ? '#f97316' : '#3b82f6'
                        },
                    },
                    z: 3,
                    markPoint: {
                        data: anomalyIndices.map((i) => ({
                            coord: [i, sizes[i]],
                            symbol: 'pin',
                            symbolSize: 30,
                            itemStyle: { color: '#f97316' },
                            label: { show: false },
                        })),
                    },
                },
            ],
        }
    }, [data])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle>Anomaly Size Analysis</DialogTitle>
                    <DialogDescription>
                        Object size over time with 3-sigma statistical bounds. Orange markers indicate anomalies.
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-2">
                    {loading ? (
                        <div className="flex items-center justify-center h-[350px] text-sm text-muted-foreground">
                            Loading chart data...
                        </div>
                    ) : data.length === 0 ? (
                        <div className="flex items-center justify-center h-[350px] text-sm text-muted-foreground">
                            No version data available
                        </div>
                    ) : (
                        <ReactECharts
                            option={chartOption}
                            style={{ height: 380 }}
                            opts={{ renderer: 'canvas' }}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
